import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback } from "react";
import { InvoiceLineItem } from "@common/types/invoice";
import { useHasAccrualLedger } from "@api/ledger.api";
import { useUpdateInvoiceLineItem } from "@api/invoice.api";
import {
    AccrualAccountCell,
    UpdateLineItemAccountParams,
} from "@components/lineItems/AccrualAccountCell";
import { LineItemFallbackAccountType } from "@common/helpers/accounting";
import { useIsInvoiceLocked } from "@components/invoices/useIsInvoiceLocked";
import { INVOICE_LOCKED_FIELD_TOOLTIP } from "@components/invoices/invoiceLockedFieldTooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    getLedgerIdFromColumnId,
    getLedgerNameFromColumnId,
} from "@lib/ledgerViewColumns";

export const AccountsReceivableCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, InvoiceLineItem, unknown>) => {
        const updateInvoiceLineItem = useUpdateInvoiceLineItem();
        const lineItem = row.original;
        const ledgerId = getLedgerIdFromColumnId(column.id);
        const ledgerName = getLedgerNameFromColumnId(column.id);
        const isLocked = useIsInvoiceLocked(lineItem.invoice);
        const hasAccrualLedger = useHasAccrualLedger(
            lineItem.invoice?.entityId,
        );

        const onUpdateLineItemAccount = useCallback(
            ({
                workspaceId,
                lineItemId,
                accountOverrides,
            }: UpdateLineItemAccountParams) =>
                updateInvoiceLineItem.mutateAsync({
                    workspaceId,
                    lineItemId,
                    accountsReceivableOverrides: accountOverrides,
                }),
            [updateInvoiceLineItem],
        );

        if (!hasAccrualLedger) {
            return <ViewTableCell />;
        }

        return (
            <AccrualAccountCell
                lineItemId={lineItem.id}
                lineItemAccountId={lineItem.accountsReceivableId}
                accountOverrides={lineItem.accountsReceivableOverrides}
                ledgerId={ledgerId}
                ledgerName={ledgerName}
                fallbackMode={
                    LineItemFallbackAccountType.invoiceAccountsReceivable
                }
                entityId={lineItem.invoice?.entityId}
                isLocked={isLocked}
                lockedTooltip={INVOICE_LOCKED_FIELD_TOOLTIP}
                onUpdateLineItemAccount={onUpdateLineItemAccount}
            />
        );
    },
);
